/**
 * Calendar Component Constants
 * V226: 통합 캘린더 컴포넌트 상수 정의
 */

import type { QuickPreset, QuickPresetOption, TimeSlot } from './types';

// ===== 요일 =====
export const WEEKDAYS_KO = ['일', '월', '화', '수', '목', '금', '토'] as const;
export const WEEKDAYS_EN = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

// ===== 빠른 선택 프리셋 =====
export const QUICK_PRESETS: Record<QuickPreset, QuickPresetOption> = {
    today: { value: 'today', label: '오늘' },
    yesterday: { value: 'yesterday', label: '어제' },
    last3Days: { value: 'last3Days', label: '최근 3일' },
    last5Days: { value: 'last5Days', label: '최근 5일' },
    lastWeek: { value: 'lastWeek', label: '최근 1주' },
    last2Weeks: { value: 'last2Weeks', label: '최근 2주' },
    lastMonth: { value: 'lastMonth', label: '최근 1개월' },
    last3Months: { value: 'last3Months', label: '최근 3개월' },
    last6Months: { value: 'last6Months', label: '최근 6개월' },
    thisMonth: { value: 'thisMonth', label: '이번 달' },
    thisYear: { value: 'thisYear', label: '올해' },
    custom: { value: 'custom', label: '직접 선택' },
};

// ===== 기본 빠른 선택 버튼 =====
export const DEFAULT_QUICK_PRESETS: QuickPreset[] = [
    'today',
    'lastWeek',
    'lastMonth',
];

// ===== 시간 슬롯 기본값 =====
export const DEFAULT_TIME_CONFIG = {
    startTime: '09:00',
    endTime: '18:00',
    interval: 30, // 분
} as const;

/**
 * 시간 슬롯 생성 함수
 */
export function generateTimeSlots(
    startTime: string = DEFAULT_TIME_CONFIG.startTime,
    endTime: string = DEFAULT_TIME_CONFIG.endTime,
    interval: number = DEFAULT_TIME_CONFIG.interval,
    closedSlots: string[] = [],
    reservedSlots: string[] = []
): TimeSlot[] {
    const slots: TimeSlot[] = [];
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);

    let currentHour = startHour;
    let currentMin = startMin;

    while (
        currentHour < endHour ||
        (currentHour === endHour && currentMin <= endMin)
    ) {
        const timeValue = `${currentHour.toString().padStart(2, '0')}:${currentMin.toString().padStart(2, '0')}`;
        const isPM = currentHour >= 12;
        const displayHour = currentHour > 12 ? currentHour - 12 : currentHour === 0 ? 12 : currentHour;
        const timeLabel = `${isPM ? '오후' : '오전'} ${displayHour}:${currentMin.toString().padStart(2, '0')}`;

        slots.push({
            value: timeValue,
            label: timeLabel,
            closed: closedSlots.includes(timeValue),
            reserved: reservedSlots.includes(timeValue),
            disabled: closedSlots.includes(timeValue),
        });

        // 다음 시간으로
        currentMin += interval;
        if (currentMin >= 60) {
            currentHour += Math.floor(currentMin / 60);
            currentMin = currentMin % 60;
        }
    }

    return slots;
}

/**
 * 기본 시간 슬롯 (09:00~18:00, 30분 간격)
 */
export const DEFAULT_TIME_SLOTS: TimeSlot[] = generateTimeSlots();

// ===== 캘린더 스타일 상수 =====
export const CALENDAR_STYLES = {
    // 날짜 셀 기본 크기
    cellSize: {
        compact: 'h-8 w-8',
        normal: 'h-10 w-10',
        large: 'h-12 w-12',
    },
    // 오늘 날짜 스타일
    today: 'bg-primary text-primary-foreground font-bold',
    // 선택된 날짜 스타일
    selected: 'bg-primary text-primary-foreground hover:bg-primary/90',
    // 범위 시작/끝 스타일
    rangeStart: 'rounded-l-md bg-primary text-primary-foreground',
    rangeEnd: 'rounded-r-md bg-primary text-primary-foreground',
    rangeMiddle: 'bg-accent text-accent-foreground',
    // 비활성화 스타일
    disabled: 'text-muted-foreground opacity-50 cursor-not-allowed',
    // 주말 스타일
    sunday: 'text-destructive',
    saturday: 'text-info',
} as const;
